import { Grid, Stack } from '@mui/material';
import { TableFilter, TFilterValues, TTableFilter } from '@components/table/types';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { EntityMetadataGetRequestQuery } from '@lio/api-types';
import { useEntityMetadata } from '@services/entityMetadata';
import { useTableStore } from '@components/table/StoreTable/TableWithStore';

import { getMetadataEntityValueHelper } from '../getMetadataEntityValueHelper';
import { generateChipLabelsFromFilters } from './generateChipLabelsFromFilters';

import { PinnedFilterChips } from '@components/table/TableCreateFiltersModal/CustomFilterToolbar/PinnedFilterChips';
import { formatFiltersForDelete } from '@components/table/TableCreateFiltersModal/CustomFilterToolbar/formatFiltersForDelete';
import { CustomFilterChips } from '@components/table/TableCreateFiltersModal/CustomFilterToolbar/CustomFilterChips';

export type CustomFilterToolbarProps = {
  appliedFilters?: TFilterValues;
  filters?: TTableFilter[];
  handleUpdateFilters: (customFilters: TFilterValues) => void;
  tableFilterTypeMetaDataBuilder: Record<
    TableFilter,
    {
      appliedFilterKey: string;
      metaDataQueryKey: keyof EntityMetadataGetRequestQuery;
      getQueryValue: ReturnType<typeof getMetadataEntityValueHelper>;
    } | null
  >;
  adjustTableHeaderHeight: () => void;
};

export const CustomFilterToolbar = ({
  appliedFilters,
  filters,
  handleUpdateFilters,
  tableFilterTypeMetaDataBuilder,
  adjustTableHeaderHeight,
}: CustomFilterToolbarProps) => {
  const [searchParamsInTheUrl, setSearchParamsInTheUrl] = useSearchParams();

  const [pinnedFilters = {}, pinnedFiltersEnabled] = useTableStore((state) => [
    state.pinnedFilters,
    state.pinnedFiltersEnabled,
  ]);

  const query = useMemo(() => {
    // build once on load to get labels for any visible pills
    if (!appliedFilters) {
      return '';
    }

    let builder: EntityMetadataGetRequestQuery = {};

    filters?.forEach((filter) => {
      const filterTypeBuilder = tableFilterTypeMetaDataBuilder[filter.type];

      if (filterTypeBuilder) {
        const values = [];
        if (appliedFilters[filterTypeBuilder.appliedFilterKey]) {
          const ids = filterTypeBuilder.getQueryValue(appliedFilters);
          if (Array.isArray(ids)) {
            values.push(...ids);
          } else {
            values.push(ids);
          }
        }
        if (pinnedFilters[filterTypeBuilder.appliedFilterKey]) {
          const pinnedIds = filterTypeBuilder.getQueryValue(pinnedFilters);
          if (Array.isArray(pinnedIds)) {
            values.push(...pinnedIds);
          } else {
            values.push(pinnedIds);
          }
        }
        builder[filterTypeBuilder.metaDataQueryKey] = values;
      }
    });

    return builder;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { entityMetadata, isLoading } = useEntityMetadata(query as EntityMetadataGetRequestQuery);

  // flat map metadata array so we can look up the labels by id
  const entityMetadataArray = Object.keys(entityMetadata ?? {}).reduce((acc: any, key) => {
    const metadata = entityMetadata && entityMetadata[key as keyof typeof entityMetadata];
    if (metadata) {
      metadata.forEach((m) => {
        acc[m.id] = {
          id: m.id,
          label: m.label,
          helperText: (m as any)?.helperText,
        };
      });
    }
    return acc;
  }, {});

  const handleRemoveFilter = (fieldKey: string, idToFind?: string) => {
    const { filtersToClear, updatedSearchParams } = formatFiltersForDelete(
      fieldKey,
      idToFind,
      appliedFilters,
      filters,
      searchParamsInTheUrl,
    );
    setSearchParamsInTheUrl(updatedSearchParams, { replace: true });
    handleUpdateFilters(filtersToClear);
  };

  // if pinned filters are enabled remove them from the custom filter list display
  let customFilters = { ...appliedFilters };
  if (pinnedFiltersEnabled) {
    Object.entries(pinnedFilters)?.forEach(([key]: any) => {
      if (customFilters && customFilters[key]) {
        delete customFilters[key];
      }
    });
  }

  const customFilterChips = generateChipLabelsFromFilters({
    filtersForChips: customFilters,
    filters,
    entityMetadataArray,
  });

  // only show pinned filters if enabled
  const pinnedFilterChips = pinnedFiltersEnabled
    ? generateChipLabelsFromFilters({
        filtersForChips: pinnedFilters,
        filters,
        entityMetadataArray,
      })
    : [];

  useEffect(() => {
    adjustTableHeaderHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedFilterChips, customFilterChips]);

  if (isLoading) {
    // prevent id flash
    return null;
  }

  if (!customFilterChips.length && !pinnedFilterChips.length) {
    return null;
  }

  return (
    <Grid container gap={1}>
      {pinnedFilterChips && pinnedFilterChips.length > 0 && (
        <Grid size={{ xs: 12 }}>
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '8px',
            }}
          >
            {pinnedFilterChips.length > 0 && (
              <PinnedFilterChips
                pinnedFilterChips={pinnedFilterChips}
                appliedFilters={appliedFilters}
                pinnedFilters={pinnedFilters}
                handleRemoveFilter={handleRemoveFilter}
                handleUpdateFilters={handleUpdateFilters}
                searchParamsInTheUrl={searchParamsInTheUrl}
                setSearchParamsInTheUrl={setSearchParamsInTheUrl}
              />
            )}
          </Stack>
        </Grid>
      )}
      {customFilterChips && customFilterChips.length > 0 && (
        <CustomFilterChips customFilterChips={customFilterChips} handleRemoveFilter={handleRemoveFilter} />
      )}
    </Grid>
  );
};
